import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner, Badge, EmptyState, Icon } from '../../../core/ui';
import { useTranslation } from '../../../core/context/TranslationContext';
import { ownerInitial } from '../format';
import { getFamily, getFamilyReceipts, getFamilyDebtForecast } from '../api/family';
import styles from './FamilyDashboardWidget.module.css';

const toIso = (d) => d.toISOString().slice(0, 10);

export default function FamilyDashboardWidget() {
  const { t, language } = useTranslation();
  const currencySymbol = language === 'tr' ? '₺' : '$';
  const [family, setFamily] = useState(null);
  const [memberSpending, setMemberSpending] = useState([]);
  const [nextMonthForecast, setNextMonthForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fam = await getFamily();
        if (cancelled) return;
        setFamily(fam);

        const now = new Date();
        const firstOfMonth = toIso(new Date(now.getFullYear(), now.getMonth(), 1));
        const today = toIso(now);

        const [receipts, forecast] = await Promise.all([
          getFamilyReceipts(firstOfMonth, today).catch(() => []),
          getFamilyDebtForecast(2).catch(() => []),
        ]);

        if (cancelled) return;

        const byMember = {};
        receipts.forEach((r) => {
          const key = r.owner?.username || 'unknown';
          byMember[key] = (byMember[key] || 0) + Number(r.totalAmount || 0);
        });
        setMemberSpending(
          Object.entries(byMember)
            .map(([username, total]) => ({ username, total }))
            .sort((a, b) => b.total - a.total)
        );

        if (forecast.length > 1) setNextMonthForecast(forecast[1]);
        else if (forecast.length === 1) setNextMonthForecast(forecast[0]);
      } catch {
        if (!cancelled) setFamily(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fmt = (val) => `${currencySymbol}${(val || 0).toFixed(2)}`;

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <Card.Title>{t('module.family')}</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className={styles.centered}>
            <Spinner label={t('loading')} />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!family) {
    return (
      <Card>
        <Card.Header>
          <Card.Title>{t('module.family')}</Card.Title>
        </Card.Header>
        <Card.Body>
          <EmptyState
            icon="fa-users"
            title={t('family.empty.cta')}
            action={
              <Link to="/family/create" className={styles.actionLink}>
                {t('family.create.submit')} →
              </Link>
            }
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className={styles.titleGroup}>
          <span className={styles.moduleEyebrow}>
            <Icon name="fa-users" size="sm" />
            {t('module.family')}
          </span>
          <Card.Title>{family.name}</Card.Title>
        </div>
        <Link to="/family" className={styles.viewAll}>
          {t('family.overview')} →
        </Link>
      </Card.Header>

      <Card.Body>
        <div className={styles.block}>
          <div className={styles.blockLabel}>{t('family.widget.thisMonthSpending')}</div>
          {memberSpending.length === 0 ? (
            <p className={styles.muted}>{t('noData')}</p>
          ) : (
            <ul className={styles.list}>
              {memberSpending.map((m) => (
                <li key={m.username} className={styles.row}>
                  <span className={styles.member}>
                    <Badge variant="accent">{ownerInitial(m.username)}</Badge>
                    <span className={styles.memberName}>{m.username}</span>
                  </span>
                  <span className="num">{fmt(m.total)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/family/receipts" className={styles.subLink}>
            {t('family.receipts')} →
          </Link>
        </div>

        <div className={styles.block}>
          <div className={styles.blockLabel}>{t('family.widget.nextMonthDebt')}</div>
          {!nextMonthForecast || Number(nextMonthForecast.totalDueTry) === 0 ? (
            <p className={styles.muted}>{t('noData')}</p>
          ) : (
            <>
              <div className={styles.row}>
                <span>{t('budget.total')}</span>
                <span className={`num ${styles.warningTotal}`}>
                  {fmt(nextMonthForecast.totalDueTry)}
                </span>
              </div>
              <ul className={styles.list}>
                {(nextMonthForecast.byMember || []).map((m) => (
                  <li key={m.userId} className={styles.row}>
                    <span className={styles.member}>
                      <Badge variant="accent">{ownerInitial(m.username)}</Badge>
                      <span className={styles.memberName}>{m.username}</span>
                    </span>
                    <span className="num">{fmt(m.amount)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Link to="/family/forecast" className={styles.subLink}>
            {t('family.forecast')} →
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
}
